import React from "react";

import {CreateStaffUserDto, UserDto} from "../../../utils/types/user";
import {Card, TagsField, H2, LockIcon, styled} from "@reside/ui";
import {useFormikContext} from "formik";

import {useFacilitiesByOrganizationQuery} from "../hooks/useFacilitiesByOrganizationQuery";
import {BackdropSpinner} from "../../../atoms/spinner";

type Props = Readonly<{
  user: UserDto;
  disabled?: boolean;
}>;

const StyledCard = styled(Card)`
  padding: ${({theme}) => theme.space[7]}px;
  margin-bottom: ${({theme}) => theme.space[5]}px;
  position: relative;
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const Space = styled.div`
  margin-top: ${({theme}) => theme.space[5]}px;
  position: relative;
`;

const DisabledOverlay = styled.div`
  pointer-events: none;
  opacity: 0.65;
  filter: saturate(25%);
`;

const BlockingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  cursor: not-allowed;
  z-index: 1;
`;

export const FacilitiesTags = ({
  user,
  disabled = false,
}: Props): JSX.Element => {
  const {
    values: {organizationId},
  } = useFormikContext<CreateStaffUserDto>();

  const {data: facilities = [], isFetching} = useFacilitiesByOrganizationQuery({
    organizationId,
  });

  const facilityOptions = facilities.map(facility => ({
    value: facility.id,
    label: facility.name,
  }));

  const userFacilityOptions = user.facilities.map(facility => ({
    value: facility.id,
    label: facility.name,
  }));

  const content = (
    <BackdropSpinner active={isFetching}>
      <TagsField
        name="facilities"
        options={disabled ? userFacilityOptions : facilityOptions}
        placeholder="Start typing facility name"
        label="Select Facility"
        testId="facilitySelect"
        disabled={disabled}
      />
    </BackdropSpinner>
  );

  return (
    <StyledCard>
      <TitleWrapper>
        <H2>Facilities</H2>
        {disabled && <LockIcon />}
      </TitleWrapper>
      <Space>
        {disabled ? (
          <>
            <DisabledOverlay>{content}</DisabledOverlay>
            <BlockingOverlay />
          </>
        ) : (
          content
        )}
      </Space>
    </StyledCard>
  );
};
